import { createSelector } from '@reduxjs/toolkit';
import { useContext, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../store';
import { AnimationState, animationStates } from '../store/slices';

import { PageTransitionContext } from './context';

import type { TypedUseSelectorHook } from 'react-redux';
import type { RootState } from '../store';

// -- actions --

export const usePageTransitionActions = () => {
  const id = useContext(PageTransitionContext);
  if (!id) {
    throw new Error('missing `<PageTransitionProvider />`');
  }

  return useMemo(() => {
    const add = (payload: {
      animationId: number;
      animationState: AnimationState.IS_PENDING | AnimationState.SHOULD_ENTER;
    }) => {
      return animationStates.actions.add({ ...payload, id });
    };

    const remove = (payload: number) => {
      return animationStates.actions.remove({ animationId: payload, id });
    };

    const set = (payload: {
      animationId: number;
      animationState: Exclude<
        AnimationState,
        | AnimationState.IS_PENDING
        | AnimationState.SHOULD_ENTER
        | AnimationState.SHOULD_LEAVE
      >;
    }) => {
      return animationStates.actions.set({ ...payload, id });
    };

    const setAll = (
      payload: AnimationState.SHOULD_ENTER | AnimationState.SHOULD_LEAVE,
    ) => {
      return animationStates.actions.setAll({ animationState: payload, id });
    };

    return { add, remove, set, setAll };
  }, [id]);
};

// -- dispatch --

export const usePageTransitionDispatch = useAppDispatch;

const pageTransitionSelector = createSelector(
  [(state: RootState) => state.animationStates, (_state, id: string) => id],
  (animationStates, id) => animationStates[id] ?? {},
);

// -- selectors --

export const usePageTransitionSelector: TypedUseSelectorHook<
  RootState['animationStates'][string]
> = (selector) => {
  const id = useContext(PageTransitionContext);
  if (!id) {
    throw new Error('missing `<PageTransitionProvider />`');
  }

  return useAppSelector((state) => selector(pageTransitionSelector(state, id)));
};

const animationStatesSelector = createSelector(
  [
    (state: RootState['animationStates'][string]) => state,
    (_state, animationIds?: number[]) => animationIds,
  ],
  (animationStates, animationIds) =>
    animationIds
      ? animationIds.map(
          (id) => animationStates[id] ?? AnimationState.IS_PENDING,
        )
      : Object.values(animationStates),
);

const animationStateSelector = createSelector(
  animationStatesSelector,
  (animationStates) => {
    if (animationStates.length === 0) return AnimationState.IS_PENDING;

    for (const possibleAnimationState of [
      AnimationState.DID_ENTER,
      AnimationState.DID_LEAVE,
      AnimationState.IS_PENDING,
    ]) {
      if (
        animationStates.every(
          (animationState) => animationState === possibleAnimationState,
        )
      ) {
        return possibleAnimationState;
      }
    }

    for (const possibleAnimationState of [
      AnimationState.IS_ENTERING,
      AnimationState.IS_LEAVING,
    ]) {
      if (
        animationStates.some(
          (animationState) => animationState === possibleAnimationState,
        )
      ) {
        return possibleAnimationState;
      }
    }

    for (const possibleAnimationState of [
      AnimationState.SHOULD_ENTER,
      AnimationState.SHOULD_LEAVE,
    ]) {
      if (
        animationStates.some(
          (animationState) => animationState === possibleAnimationState,
        )
      ) {
        return possibleAnimationState;
      }
    }

    console.warn(new TypeError('unreachable'));
    console.warn(animationStates);

    return AnimationState.IS_PENDING;
  },
);

export const useAnimationStateSelector = (animationIds?: number[]) =>
  usePageTransitionSelector((state) =>
    animationStateSelector(state, animationIds),
  );

export { PageTransitionProvider } from './provider';
export { AnimationState } from '../store/slices/animationStates';
